import React, { Suspense, lazy, useEffect, useState } from "react";

import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";

import { GridActionsCellItem } from "@mui/x-data-grid";
import { useUserAuth } from "../../contexts/authContext";
import { useEnv } from "../../contexts/envContext";
import { useMap } from "../../contexts/mapContext";
import { CustomPagination } from "../../customPagination";
import { ConvertISOToDate, GetColorFromState } from "../../util";
import {
    canUserEditOrDelete,
    getFlightTransitions,
    getOperationPriority,
    getWaypointsDownloadable,
    handleDownloadOperations,
    handleFlightStatusChange
} from "./opUtil";

import MapPreview from "../../map/mapPreview";
import ExportFlightWaypointsMenu from "./exportFlightWaypointsDialog";

import * as CONSTANT from "../operations/opConstants";
import { FlightInfoDialog } from "./flightInfoDialog";
import ResponsiveGrid from "../../grid/responsiveGrid";

const LAANCDetailsDialog = lazy(() => import("./laancDetailsDialog"));

export function Operations() {
    const [showMap, setShowMap] = useState(false);
    const [operationColumns, setOperationColumns] = useState([]);
    const [infoFlight, setInfoFlight] = useState(null);
    const [flightInfoOpen, setFlightInfoOpen] = useState(false);

    const [operationsAreSelected, setOperationsAreSelected] = useState(false);
    const [selectedPublishedOperations, setSelectedPublishedOperations] = useState([]);

    const [flightDetailsVolume, setFlightDetailsVolume] = useState(null);
    const [flightDetailsDialogOpen, setFlightDetailsDialogOpen] = useState(false);

    const [downloadWaypointDialogOpen, setDownloadWaypointDialogOpen] = useState(false);
    const [downloadWaypointOperation, setDownloadWaypointOperation] = useState(null);

    const { publishedOperations } = useMap();
    const { laancOn } = useEnv();
    const { user, userMapSettings, getOrganizationByID, userOperationalStates, handleFailedFetch } = useUserAuth();

    const initialColumns = [
        {
            field: "info",
            headerName: "",
            minWidth: 50,
            maxWidth: 50,
            flex: 1,
            display: "flex",
            renderCell: (cellValues) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
                    <IconButton size="medium" onClick={(event) => handleInfoClick(event, cellValues)} id={`info-${cellValues.row.volumeId}`} sx={{ p: 0 }}>
                        <InfoIcon />
                    </IconButton>
                </Box>
            )
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 175,
            flex: 1,
            editable: false
        },
        {
            field: "state",
            headerName: "Status",
            minWidth: 190,
            flex: 1,
            type: "singleSelect",
            editable: true,
            valueOptions: (cellValues) => getFlightTransitions(cellValues.row),
            renderCell: (cellValues) => {
                return (
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: "bold",
                            color: GetColorFromState(cellValues.row.state, userOperationalStates)
                        }}
                    >
                        {cellValues.row.state}
                    </div>
                );
            }
        },
        {
            field: "time_start",
            headerName: "Start",
            type: "dateTime",
            minWidth: 200,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "time_end",
            headerName: "End",
            type: "dateTime",
            minWidth: 200,
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "organization_id",
            headerName: "Org",
            editable: false,
            minWidth: 75,
            maxWidth: 75,
            flex: 1,
            renderCell: (cellValues) => {
                const org = getOrganizationByID(cellValues.formattedValue);
                if (org != undefined) {
                    return <div>{org.name}</div>;
                }
            }
        },
        {
            field: "priority",
            headerName: "Priority",
            flex: 1,
            editable: false,
            minWidth: 100,
            valueGetter: (value, row) => `${getOperationPriority(row)}`
        },
        {
            field: "version",
            headerName: "Ver",
            minWidth: 40,
            maxWidth: 40,
            flex: 1,
            editable: false,
            valueGetter: (value, row) => `${row.version}`
        },
        {
            field: "conflicted",
            headerName: "Conflicted",
            align: "center",
            minWidth: 80,
            maxWidth: 80,
            flex: 1,
            editable: false,
            renderCell: (cellValues) => {
                if (cellValues.formattedValue) {
                    return <div style={{ fontWeight: "bold", color: "#CB4C4E" }}>Yes</div>;
                } else {
                    return <div>No</div>;
                }
            }
        }
    ];

    const laancColumns = [
        {
            field: "laanc_state",
            headerName: "LAANC",
            minWidth: 210,
            flex: 1,
            editable: false,
            display: "flex",
            renderCell: (cellValues) => {
                const isOwnOp = user.id === cellValues.row.created_user_id ? true : false;
                if (Object.prototype.hasOwnProperty.call(cellValues.row, "laanc")) {
                    return (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Box>{cellValues.row.laanc.state.replace(/_/g, " ")}</Box>
                            {isOwnOp && cellValues.row.faa_approval_required === true ? (
                                cellValues.row.laanc.state === CONSTANT.LAANC_RESCINDED_AWAITING ||
                                cellValues.row.laanc.state === CONSTANT.LAANC_INVALID_AWAITING ? (
                                    <IconButton
                                        size="medium"
                                        onClick={() => handleDetailsButtonClicked(cellValues.row)}
                                        id={`laanc-${cellValues.row.volumeId}`}
                                    >
                                        <Badge badgeContent={"!"} color={"error"}>
                                            <ArticleIcon />
                                        </Badge>
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        size="medium"
                                        onClick={() => handleDetailsButtonClicked(cellValues.row)}
                                        id={`laanc-${cellValues.row.volumeId}`}
                                    >
                                        <ArticleIcon />
                                    </IconButton>
                                )
                            ) : (
                                <></>
                            )}
                        </Box>
                    );
                } else return <div>NOT REQUIRED</div>;
            }
        }
    ];

    const actionsColumn = [
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const canDelete = canUserEditOrDelete(row, user);
                const canDownloadWaypoints = getWaypointsDownloadable(row, user);
                const actions = [];
                if (canDownloadWaypoints) {
                    actions.push(
                        <GridActionsCellItem
                            key={row.id}
                            icon={<DownloadIcon />}
                            label="Download"
                            onClick={() => {
                                setDownloadWaypointDialogOpen(true);
                                setDownloadWaypointOperation(row);
                            }}
                        />
                    );
                }
                if (canDelete) {
                    actions.push(
                        <GridActionsCellItem
                            key={row.id}
                            icon={<DeleteIcon />}
                            label="Delete"
                            id={`cancel-${row.volumeId}`}
                            onClick={() => handleFlightStatusChange(row, "DELETED", user, handleFailedFetch)}
                        />
                    );
                }
                return actions;
            }
        }
    ];

    useEffect(() => {
        if (operationsAreSelected === false) {
            setSelectedPublishedOperations(publishedOperations);
        }
    }, [publishedOperations, selectedPublishedOperations, operationsAreSelected]);

    //Sets up the columns for the Operation Grid based on Login
    useEffect(() => {
        const columns = initialColumns;
        const timeEndColumn = columns.findIndex((column) => column.field === "time_end");
        if (laancOn === "true" && timeEndColumn !== -1) {
            columns.splice(timeEndColumn + 1, 0, ...laancColumns);
        }
        columns.push(...actionsColumn);
        setOperationColumns(columns);
    }, []);

    const handleStatusChange = (updatedRow, originalRow) => {
        if (originalRow.state === updatedRow.state || handleFlightStatusChange(originalRow, updatedRow.state, user, handleFailedFetch)) {
            return updatedRow;
        }
        return originalRow;
    };

    const handleInfoClick = (event, cellValues) => {
        event.stopPropagation();
        setInfoFlight(cellValues.row);
        setFlightInfoOpen(true);
    };

    const handleDetailsButtonClicked = (operation) => {
        setFlightDetailsDialogOpen(true);
        setFlightDetailsVolume(operation);
    };

    const handleCheckboxSelected = (ids) => {
        if (ids.length > 0 && showMap === false) {
            setShowMap(true);
        }
        const selected = publishedOperations.filter(({ flight_uuid }) => {
            return ids.includes(flight_uuid);
        });
        if (selected.length > 0) {
            setOperationsAreSelected(true);
            setSelectedPublishedOperations(selected);
        } else {
            setOperationsAreSelected(false);
            setSelectedPublishedOperations(publishedOperations);
        }
    };

    const handleCheckCellEditable = ({ row }) => {
        return canUserEditOrDelete(row, user);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "column" },
                width: "100%",
                height: "100%",
                justifyContent: { xs: "flex-end", sm: "flex-start" }
            }}
        >
            <Paper
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "column" },
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1, sm: 2 },
                    borderTopRightRadius: 0,
                    botderTopLeftRadius: 0,
                    height: { xs: "100%", sm: "578px" },
                    flexShrink: { xs: "unset", sm: 0 }
                }}
            >
                <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <ResponsiveGrid
                        primaryColumns={["name", "time_start"]}
                        checkboxSelection
                        rows={publishedOperations}
                        processRowUpdate={(updatedRow, originalRow) => handleStatusChange(updatedRow, originalRow)}
                        isCellEditable={handleCheckCellEditable}
                        disableVirtualization
                        disableRowSelectionOnClick
                        columns={operationColumns}
                        getRowId={(row) => row.flight_uuid}
                        pagination
                        slots={{ pagination: CustomPagination }}
                        onRowSelectionModelChange={handleCheckboxSelected}
                        sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={() => setShowMap((prev) => !prev)} variant="contained" color="primary" size="small">
                        {showMap ? "Hide Map" : "Preview on Map"}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        id="download"
                        onClick={() => handleDownloadOperations(selectedPublishedOperations, userMapSettings)}
                        size="small"
                    >
                        Download KML
                    </Button>
                </Box>
            </Paper>

            {showMap ? (
                <Paper sx={{ flexShrink: 0, height: { xs: "40%", sm: "400px" }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 0 }, pb: { xs: 0, sm: 2 } }}>
                    <MapPreview selected_published_flights={selectedPublishedOperations} />
                </Paper>
            ) : (
                <></>
            )}

            {flightInfoOpen ? <FlightInfoDialog flight={infoFlight} flightInfoOpen={flightInfoOpen} setFlightInfoOpen={setFlightInfoOpen} /> : <></>}

            {flightDetailsDialogOpen ? (
                <Suspense fallback={<></>}>
                    <LAANCDetailsDialog
                        flightDetailsVolume={flightDetailsVolume}
                        flightDetailsDialogOpen={flightDetailsDialogOpen}
                        publishedOperations={publishedOperations}
                        setFlightDetailsVolume={setFlightDetailsVolume}
                        setFlightDetailsDialogOpen={setFlightDetailsDialogOpen}
                    />
                </Suspense>
            ) : (
                <></>
            )}
            {downloadWaypointDialogOpen === true ? (
                <ExportFlightWaypointsMenu
                    downloadWaypointDialogOpen={downloadWaypointDialogOpen}
                    setDownloadWaypointDialogOpen={setDownloadWaypointDialogOpen}
                    downloadWaypointOperation={downloadWaypointOperation}
                    setDownloadWaypointOperation={setDownloadWaypointOperation}
                />
            ) : (
                <></>
            )}
        </Box>
    );
}
